import React, { useState, useCallback, useMemo, useEffect } from 'react';
import type { ReactNode } from 'react';
import { isType } from 'variant';
import { Box } from 'ink';
import { InputState } from '../../input-state.js';
import { InputLabel } from '../InputLabel.js';
import { Placeholder } from './Placeholder.js';
import { Text } from '../Text.js';
import { Input } from '../TextInput/Input.js';
import { SelectOptionAvailability } from './types.js';
import { GroupsList } from './GroupsList.js';
import type { SelectGroup, SelectOption } from './types.js';
import { getListItems } from './helpers.js';

const DEFAULT_MAX_HEIGHT = 20;

export interface SelectGroupsInputProps<T> {
  label: string;
  info?: ReactNode;
  placeholder?: SelectOption<T>;
  groups: Array<SelectGroup<T>>;
  value?: InputState<SelectOption<T>>;
  onSubmit: (item: SelectOption<T>) => void;
  maxHeight?: number;
  width?: number | string;
  hideScroll?: boolean;
}

export function SelectGroupsInput<T>({
  label,
  info,
  groups,
  onSubmit,
  maxHeight = DEFAULT_MAX_HEIGHT,
  hideScroll,
  width,
  placeholder,
  value,
}: SelectGroupsInputProps<T>) {
  if (!groups.length) {
    throw new Error('SelectGroupsInput: groups cannot be empty');
  }

  const [inputState, setInputState] = useState<InputState<SelectOption<T>>>(
    () => value ?? InputState.NotSubmitted(placeholder)
  );
  const [searchRegExp, setSearchRegExp] = useState<RegExp>();

  const filteredGroups = useMemo(() => {
    if (!searchRegExp) {
      return groups;
    }

    return groups.reduce<Array<SelectGroup<T>>>((acc, group) => {
      if (group.title.search(searchRegExp) !== -1) {
        acc.push({ ...group });
      } else {
        const filteredItems = group.items.filter(
          (item) => item.title.search(searchRegExp) !== -1
        );
        if (filteredItems.length) {
          acc.push({ ...group, items: filteredItems });
        }
      }

      return acc;
    }, []);
  }, [groups, searchRegExp]);

  const listItems = useMemo(
    () => getListItems(filteredGroups),
    [filteredGroups]
  );

  const handleItemSelection = useCallback(
    (selectedItem: SelectOption<T>) => {
      if (
        !isType(selectedItem.availability, SelectOptionAvailability.Disabled)
      ) {
        setInputState(InputState.Submitted(selectedItem));
        onSubmit(selectedItem);
      }
    },
    [onSubmit]
  );

  useEffect(() => {
    if (value) {
      setInputState(value);
    }
  }, [value, setInputState]);

  const handleSearchPhraseChange = useCallback((searchPhrase: string) => {
    // escape the search phrase to prevent errors in the RegExp, e.g. if the user types `\`
    const escapedSearchPhrase = searchPhrase.replaceAll(
      /([.?*+^$[\]\\(){}|-])/g,
      '\\$1'
    );
    setSearchRegExp(
      searchPhrase ? new RegExp(escapedSearchPhrase, 'gi') : undefined
    );
  }, []);
  const lastItem = listItems.at(-1);
  const shouldShowSearchInput = searchRegExp
    ? true
    : lastItem && lastItem.rowIndex > maxHeight;

  return (
    <>
      <Box marginBottom={1}>
        <Text>
          <InputLabel>{label}</InputLabel>{' '}
          {info && isType(inputState, InputState.NotSubmitted) && (
            <Text skin="secondary">{info}</Text>
          )}
        </Text>
        {isType(inputState, InputState.NotSubmitted) &&
          shouldShowSearchInput && (
            <Text skin="secondary">
              <Input
                onChange={handleSearchPhraseChange}
                showCursor={true}
                placeholder="Type to search..."
              />
            </Text>
          )}
      </Box>
      <Placeholder inputState={inputState} />
      {isType(inputState, InputState.NotSubmitted) &&
        (listItems.length ? (
          <GroupsList<T>
            listItems={listItems}
            highlightPattern={searchRegExp}
            maxHeight={maxHeight}
            width={width}
            hideScroll={hideScroll}
            onItemSelect={handleItemSelection}
          />
        ) : (
          <Box paddingBottom={1} paddingX={2}>
            <Text skin="secondary">No results found.</Text>
          </Box>
        ))}
    </>
  );
}
